import {ChangeDetectionStrategy, Component, inject, signal,} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {TranslatePipe} from '@ngx-translate/core';
import {forkJoin} from 'rxjs';
import {UrlDto, VideoControllerService, VideoDto,} from '@api/utisha-backend';
import {MessageService} from '../../../services/message/message.service';
import {NavigationService} from '../../../services/nav/navigation.service';
import {ZonedDatePipe} from '../../../pipes/zoned-date-pipe';
import {
  UtCardComponent,
  UtEmptyStateComponent,
  UtSkeletonComponent,
  UtToolbarComponent,
} from '../../../ui';

/**
 * Teacher video player — plays a single video by UUID.
 *
 * Fetches the signed download URL **and** the video metadata in parallel
 * (via `getVideos({ uuid })`, since there's no `getVideoByUuid` in the
 * generated client), so we can render the title, upload date, and tags
 * in the `ut-toolbar` chrome around the native `<video>` element.
 */
@Component({
  selector: 'app-video-player',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './video-player.component.html',
  styleUrls: ['./video-player.component.scss'],
  imports: [
    TranslatePipe,
    ZonedDatePipe,
    UtToolbarComponent,
    UtCardComponent,
    UtSkeletonComponent,
    UtEmptyStateComponent,
  ],
})
export class VideoPlayerComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly videoService = inject(VideoControllerService);
  private readonly messageService = inject(MessageService);
  protected readonly nav = inject(NavigationService);

  protected readonly videoUuid = signal<string | undefined>(undefined);
  protected readonly url = signal<UrlDto | undefined>(undefined);
  protected readonly video = signal<VideoDto | undefined>(undefined);
  protected readonly loading = signal(true);
  protected readonly error = signal(false);

  constructor() {
    this.route.params.subscribe((params) => {
      if (params.uuid) {
        this.videoUuid.set(params.uuid);
        this.refresh();
      }
    });
  }

  private refresh(): void {
    const uuid = this.videoUuid();
    if (!uuid) return;
    this.loading.set(true);
    this.error.set(false);
    forkJoin([
      this.videoService.getVideoDownloadUrl(uuid),
      this.videoService.getVideos({ uuid, page: 0, pageSize: 1 }),
    ]).subscribe({
      next: ([url, page]) => {
        this.url.set(url);
        this.video.set(page.videos?.[0]);
        this.loading.set(false);
      },
      error: (err) => {
        this.messageService.apiError(err);
        this.error.set(true);
        this.loading.set(false);
      },
    });
  }

  protected back(): void {
    this.nav.videoOverview();
  }
}
